import React, { useEffect, useRef } from "react";
import { Id, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./LeafToastMessage.scss";
import { IconCloseThick } from "../../icons/IconCloseThick/IconCloseThick";

export type Props = {
  showToast: boolean;
  htmlContent?: React.ReactNode;
  message?: string;
  position?: "top-right" | "top-center" | "top-left" | "bottom-right" | "bottom-center" | "bottom-left";
  autoClose?: number; // in milliseconds
  hideProgressBar?: boolean;
  closeOnClick?: boolean;
  pauseOnHover?: boolean;
  draggable?: boolean;
  progress?: number;
  theme?: "success" | "info" | "warning" | "error";
  className?: string;
  toastId?: string;
};

const LeafToastMessage = ({
  showToast,
  htmlContent,
  message,
  position = "top-center",
  autoClose = 4000,
  hideProgressBar = false,
  closeOnClick = true,
  pauseOnHover = true,
  draggable = false,
  progress,
  theme = "success",
  className,
  toastId,
}: Props) => {
  const activeToastIdRef = useRef<Id | undefined>(undefined);
  const wasShowingRef = useRef(false);

  useEffect(() => {
    if (!showToast) {
      if (activeToastIdRef.current) {
        toast.dismiss(activeToastIdRef.current);
        activeToastIdRef.current = undefined;
      }
      wasShowingRef.current = false;
      return;
    }

    const toastContent = (
      <div>
        {htmlContent}
        {message}
      </div>
    );
    const toastOptions = {
      position,
      autoClose,
      hideProgressBar,
      closeOnClick,
      pauseOnHover,
      draggable,
      progress,
      className,
      toastId,
      closeButton: () => (
        <button
          className="custom-close-button"
          data-testid="toast-close-button"
          onClick={() => toast.dismiss(activeToastIdRef.current)}
          aria-label="Close notification"
        >
          <IconCloseThick height="0.875rem" width="0.625rem" theme={theme} />
        </button>
      ),
    };

    if (wasShowingRef.current && activeToastIdRef.current === toastId) {
      toast.update(activeToastIdRef.current, { render: toastContent, ...toastOptions });
      return;
    }

    if (activeToastIdRef.current) toast.dismiss(activeToastIdRef.current);

    const displayedToastId = toast[theme](toastContent, toastOptions);
    activeToastIdRef.current = toastId ?? displayedToastId;
    wasShowingRef.current = true;
  }, [
    showToast,
    theme,
    htmlContent,
    message,
    position,
    autoClose,
    hideProgressBar,
    closeOnClick,
    pauseOnHover,
    draggable,
    progress,
    className,
    toastId,
  ]);

  return null;
};

export default LeafToastMessage;
